import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { formatAmount } from "@/context/LocaleContext";

export default function Orders() {
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => (await api.get("/orders")).data,
    refetchInterval: 8000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  if (isLoading) return <div className="max-w-4xl mx-auto px-6 pt-28 pb-24"><div className="h-64 shimmer" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <h1 className="font-serif text-5xl tracking-tight mb-12">Your orders</h1>
      {orders.length === 0 ? (
        <div className="py-20 text-center">
          <Package className="w-8 h-8 mx-auto text-aura-muted mb-4" />
          <p className="text-aura-muted mb-6">No orders yet.</p>
          <Link to="/shop" className="px-8 py-3 bg-aura-accent text-white text-sm inline-block">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-6" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="bg-aura-surface border border-aura-border p-6">
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-aura-border">
                <div>
                  <div className="text-xs text-aura-muted">Order #{o.id.slice(0, 8)}</div>
                  <div className="text-sm mt-1">{new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <span className="text-[10px] tracking-luxe uppercase bg-aura-sand px-2.5 py-1">{o.status}</span>
                  <div className="font-mono mt-2">{formatAmount(o.total, (o.currency || "usd").toUpperCase())}</div>
                </div>
              </div>
              <div className="space-y-3">
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-16 bg-aura-sand overflow-hidden shrink-0">
                      {it.image && <img src={imgUrl(it.image)} alt={it.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 text-sm">{it.title}</div>
                    <div className="text-sm text-aura-muted">×{it.quantity}</div>
                    <div className="font-mono text-sm">{formatAmount(it.price, (o.currency || it.listing_currency || "usd").toUpperCase())}</div>
                  </div>
                ))}
              </div>
              {o.shipping_address && (
                <p className="mt-4 text-sm text-aura-muted">
                  Ships to {o.shipping_address.name}, {o.shipping_address.line1}, {o.shipping_address.city} {o.shipping_address.postal}, {o.shipping_address.country}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
